import { useCallback, useEffect, useRef, useState } from 'react';
import type { Resultado } from '../../acesso';
import { lerVersao } from '../../administracao';
import './config.css';

/**
 * Peças comuns das telas 34, 35 e 36 (Configurações, Integrações, Saúde e auditoria).
 *
 * - Leitura com os estados de verdade: carregando, sem acesso, erro e dado — nunca uma lista
 *   vazia fingindo que não há nada.
 * - Hora sempre no fuso da operação (Brasília), não no do navegador.
 * - O selo do ambiente vem de `/api/version`: fora de produção ele diz o ambiente real e nunca
 *   "PRODUÇÃO".
 */

export function useLeitura<T>(ler: () => Promise<Resultado<T>>, chave: string) {
  const [d, setD] = useState<T | null>(null);
  const [falha, setFalha] = useState<'erro' | 'sem-acesso' | null>(null);
  const lerRef = useRef(ler);
  lerRef.current = ler;
  const recarregar = useCallback(() => {
    setFalha(null);
    void lerRef.current().then((r) => {
      if (r.ok) setD(r.dados);
      else setFalha(r.falha.tipo === 'sem-acesso' ? 'sem-acesso' : 'erro');
    });
  }, []);
  useEffect(() => {
    setD(null);
    recarregar();
  }, [chave, recarregar]);
  return { d, falha, recarregar };
}

const FUSO = 'America/Sao_Paulo';
const partes = (iso: string) => {
  const d = new Date(iso);
  const f = new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const p = (t: string) => f.find((x) => x.type === t)?.value ?? '';
  return {
    dia: `${p('day')}/${p('month')}`,
    ano: p('year'),
    hora: `${p('hour')}:${p('minute')}`,
    chave: `${p('year')}-${p('month')}-${p('day')}`,
  };
};
/** "14:58" */
export const hora = (iso: string) => partes(iso).hora;
/** "22/09" */
export const diaMes = (iso: string) => partes(iso).dia;
/** "22/09 14:58" */
export const diaHora = (iso: string) => {
  const x = partes(iso);
  return `${x.dia} ${x.hora}`;
};
/** "hoje 14:58", "ontem 07:40" ou "22/09 14:58" */
export function quando(iso: string) {
  const x = partes(iso);
  const hoje = partes(new Date().toISOString()).chave;
  const ontem = partes(new Date(Date.now() - 864e5).toISOString()).chave;
  if (x.chave === hoje) return `hoje ${x.hora}`;
  if (x.chave === ontem) return `ontem ${x.hora}`;
  return `${x.dia} ${x.hora}`;
}
/** O dia (em Brasília) de um instante, como o filtro de data pede: "2026-09-24". */
export const diaISO = (iso: string) => partes(iso).chave;
/** O dia de hoje em Brasília. */
export const hojeISO = () => partes(new Date().toISOString()).chave;

/** Desenhos do design que a família única de ícones ainda não tem (plug, pulso, ajuda, copiar). */
const LOCAIS = {
  plug: 'M7 2.8v3.6M13 2.8v3.6M5 6.4h10v3.2a5 5 0 0 1-10 0ZM10 14.6v2.6',
  pulse: 'M2.6 10h3.2l2-5 4.4 10 2-5h3.2',
  help: 'M10 17.2a7.2 7.2 0 1 0 0-14.4 7.2 7.2 0 0 0 0 14.4ZM7.8 7.8a2.3 2.3 0 0 1 4.4 1c0 1.5-2.2 1.9-2.2 3.2M10 14.2v.1',
  copy: 'M7 7h9.4v9.4H7zM4 13V3.6h9.4',
} as const;
export function IconeLocal({
  nome,
  tamanho = 17,
  traco = 1.6,
}: {
  nome: keyof typeof LOCAIS;
  tamanho?: number;
  traco?: number;
}) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={traco}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: 'none' }}
      aria-hidden="true"
    >
      <path d={LOCAIS[nome]} />
    </svg>
  );
}

export type Versao = {
  readonly app: string | null;
  readonly versao: string | null;
  readonly ambiente: string | null;
  readonly commit: string | null;
  readonly node: string | null;
  readonly fuso: string | null;
};
const texto = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null);
/** `/api/version` responde { app, version, environment, commit, node, timezone }. */
export function useVersao() {
  const l = useLeitura(lerVersao, 'versao');
  const v: Versao | null = l.d
    ? {
        app: texto(l.d['app']),
        versao: texto(l.d['version']),
        ambiente: texto(l.d['environment']),
        commit: texto(l.d['commit']),
        node: texto(l.d['node']),
        fuso: texto(l.d['timezone']),
      }
    : null;
  return { v, falhou: l.falha !== null };
}

/** O nome do ambiente como a pessoa lê. Desconhecido fica desconhecido. */
export function nomeDoAmbiente(amb: string | null) {
  if (amb === 'production') return { rotulo: 'Produção', tom: 'ok' as const };
  if (amb === 'development') return { rotulo: 'Desenvolvimento', tom: 'aviso' as const };
  if (amb === 'test') return { rotulo: 'Teste', tom: 'aviso' as const };
  if (amb) return { rotulo: amb, tom: 'aviso' as const };
  return { rotulo: 'Ambiente não informado', tom: 'neutro' as const };
}

export function SeloDoAmbiente({ v, falhou }: { v: Versao | null; falhou: boolean }) {
  if (!v && !falhou) return <span className="cg-amb cg-amb-neutro">…</span>;
  const a = nomeDoAmbiente(v?.ambiente ?? null);
  return (
    <span className={`cg-amb cg-amb-${a.tom}`} title="Ambiente informado pelo servidor">
      {a.rotulo.toUpperCase()}
    </span>
  );
}
